import { Button } from "@mui/material";
import React, { ReactNode, useEffect } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { Card } from "../Card";
import { greyColor, primaryColor } from "../colors";
import { Content } from "../Content";
import { GradientBox } from "../GradientBox";
import { Background } from "../home/Background";
import { HomeHeader } from "../home/Header";
import { mobileQuery } from "../MediaQuery";
import analytics from "../services/analytics";
import { ShareFooter, ShareMenu } from "../ShareTemplate";
import { SizedBox } from "../SizedBox";
import {
  AndroidStoreButton,
  AppStoreButton,
  StoreButtonsBlock,
} from "../StoreButtonsBlock";
import { translation } from "../translations";
import { Header4, Header6 } from "../typography";

type ParamTypes = {
  id: string;
};

function appLinkForInvite(inviteId: string): string {
  return `vikalife://invite/${inviteId}`;
}

const LeagueInvite: React.FC = (): JSX.Element => {
  const { id } = useParams<ParamTypes>();
  if (id === undefined) {
    throw Error("Unexpectedly unset id");
  }

  const image = `https://firebasestorage.googleapis.com/v0/b/vika-app/o/invite-${id}.png?alt=media`;

  useEffect(() => {
    analytics.logPageView("leagueInvite");
    location.href = appLinkForInvite(id);
  }, []);

  const os = getOS();
  const isMobile = os === OS.Android || os === OS.iOS;

  return (
    <Background>
      <ShareMenu />
      <GradientBox>
        <HeaderImageContainer>
          <HeaderImage src="/league-header.png" />
        </HeaderImageContainer>
      </GradientBox>
      <Content>
        <TopWrapper>
          <PreviewImage src={image} />
          <StoreBlock id={id} />
          <SizedBox mobileHeight={16} />
          {isMobile ? (
            <ApplinkButton id={id} text={translation.alreadyHaveVika} />
          ) : (
            ""
          )}
        </TopWrapper>
      </Content>
      <SizedBox desktopHeight={88} mobileHeight={48} />
      <HomeHeader includeNavigation={false} learnMore={true} />
      <ShareFooter />
    </Background>
  );
};

const StoreBlock: React.FC<{
  id: string;
}> = (props): JSX.Element => {
  const os = getOS();
  const utmContentOverride = `league-invite-${props.id}`;
  switch (os) {
    case OS.Android:
      return <AndroidStoreButton utmContentOverride={utmContentOverride} />;
    case OS.iOS:
      return (
        <TopWrapper>
          <Header4 color={primaryColor} center>
            {translation.howToJoinLeague}
          </Header4>
          <JoiningStep
            n={1}
            line={translation.installVika}
            bottomElement={
              <div style={{ marginTop: 16 }}>
                <AppStoreButton />
              </div>
            }
          />
          <JoiningStep
            n={2}
            line={translation.tapButtonBelow}
            bottomElement={
              <div style={{ marginTop: 16 }}>
                <ApplinkButton id={props.id} text={translation.joinLeague} />
              </div>
            }
          />
        </TopWrapper>
      );
    default:
      return (
        <TopWrapper>
          <DownloadHeader>{translation.useFreeApp}</DownloadHeader>
          <StoreButtonsBlock
            showStoreButtonsCallToAction={false}
            utmContentOverride={utmContentOverride}
          />
        </TopWrapper>
      );
  }
};

const ApplinkButton: React.FC<{
  id: string;
  text: string;
}> = (props): JSX.Element => (
  <Button variant="outlined" color="primary" href={appLinkForInvite(props.id)}>
    <Header6 color={primaryColor}>{props.text}</Header6>
  </Button>
);

const JoiningStep: React.FC<{
  n: number;
  line: string;
  bottomElement: ReactNode;
}> = (props): JSX.Element => {
  return (
    <JoiningStepCard>
      <div>
        <SizedBox width={60} />
        <JoiningStepNumber>{props.n}</JoiningStepNumber>
      </div>
      <div
        style={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          alignItems: "center",
          placeContent: "center",
        }}
      >
        <Header4 center>{props.line}</Header4>
        {props.bottomElement}
      </div>
    </JoiningStepCard>
  );
};

const JoiningStepCard = styled(Card)`
  display: flex;
  flex-direction: row;
  width: 300px;
  padding: 0px;
  margin: 8px;
  box-sizing: border-box;
`;

const JoiningStepNumber = styled.h3`
  font-family: "Work Sans", sans-serif;
  font-size: 48px;
  text-align: center;
  color: ${primaryColor};
`;

const HeaderImageContainer = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;
  margin-top: 20px;
`;

const HeaderImage = styled.img`
  height: 231px;
  padding-top: 7px;
`;

const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PreviewImage = styled.img`
  width: 600px;
  height: 316px;
  border-radius: 4px;
  margin: 64px 0 32px 0;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.3);
  ${mobileQuery`
    width: 300px;
    height: 158px;
    margin-bottom: 16px;
  `}
`;

const DownloadHeader = styled.p`
  font-size: 16px;
  font-weight: 500;
  color: ${greyColor};
  margin: 0 0 8px 0;
`;

enum OS {
  Android,
  iOS,
  other,
}

function getOS() {
  const userAgent = navigator.userAgent || navigator.vendor;
  if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)) {
    return OS.iOS;
  }
  if (userAgent.match(/Android/i)) {
    return OS.Android;
  }
  return OS.other;
}

export default LeagueInvite;
